/**
 * Utility functions for generating structured data (JSON-LD schema markup)
 * for SEO enhancement across the Shoppers Discount Club Directory
 */

/**
 * Generate LocalBusiness schema for retailer listings
 */
export const generateLocalBusinessSchema = (business) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `https://shoppersdiscountclub.directory/business/${business.id}`,
    "name": business.name,
    "description": business.description,
    "url": business.website || `https://shoppersdiscountclub.directory/business/${business.id}`,
    "telephone": business.phone || business.contact_phone,
    "email": business.email || business.contact_email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": business.address,
      "addressLocality": business.city || extractCity(business.address),
      "addressRegion": business.region || "UK",
      "addressCountry": "GB"
    }
  };

  // Add optional properties only if they exist
  if (business.image_url || business.logo_url) {
    schema.image = business.image_url || business.logo_url;
  }

  if (business.latitude && business.longitude) {
    schema.geo = {
      "@type": "GeoCoordinates",
      "latitude": business.latitude,
      "longitude": business.longitude
    };
  }

  if (business.opening_hours) {
    schema.openingHours = Array.isArray(business.opening_hours) 
      ? business.opening_hours 
      : [business.opening_hours];
  }

  if (business.price_range) {
    schema.priceRange = business.price_range;
  }

  // Add social media links if available
  const socialLinks = [];
  if (business.facebook_url) socialLinks.push(business.facebook_url);
  if (business.instagram_url) socialLinks.push(business.instagram_url);
  if (business.twitter_url) socialLinks.push(business.twitter_url);
  if (socialLinks.length > 0) {
    schema.sameAs = socialLinks;
  }

  // Add rating if available
  if (business.rating && business.review_count) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": business.rating,
      "reviewCount": business.review_count,
      "bestRating": "5",
      "worstRating": "1"
    };
  }

  // Add discount offers for SEO
  if (business.discount_type && (business.flat_discount_amount || business.category_discounts)) {
    const offers = [];
    
    if (business.discount_type === 'flat' && business.flat_discount_amount) {
      offers.push({
        "@type": "Offer",
        "name": `£${business.flat_discount_amount} off all purchases`,
        "description": business.minimum_expenditure 
          ? `£${business.flat_discount_amount} discount on all purchases with minimum spend of £${business.minimum_expenditure}`
          : `£${business.flat_discount_amount} discount on all purchases`,
        "discountAmount": {
          "@type": "MonetaryAmount",
          "currency": "GBP",
          "value": business.flat_discount_amount
        },
        "eligibleCustomerType": "Everyone",
        "validThrough": "2025-12-31" // Default to end of year, can be made dynamic
      });
    } else if (business.discount_type === 'percentage' && business.category_discounts) {
      Object.entries(business.category_discounts).forEach(([category, discount]) => {
        if (discount && discount > 0) {
          const categoryNames = {
            students: 'Students',
            pensioners: 'Pensioners',
            government_workers: 'Government Workers',
            nhs_key_workers: 'NHS and Key Workers',
            general_public: 'General Public'
          };
          
          offers.push({
            "@type": "Offer",
            "name": `${discount}% off for ${categoryNames[category] || category}`,
            "description": business.minimum_expenditure
              ? `${discount}% discount for ${categoryNames[category] || category} with minimum spend of £${business.minimum_expenditure}`
              : `${discount}% discount for ${categoryNames[category] || category}`,
            "discountPercentage": discount,
            "eligibleCustomerType": categoryNames[category] || category,
            "validThrough": "2025-12-31"
          });
        }
      });
    }
    
    if (offers.length > 0) {
      schema.offers = offers;
    }
  }

  return schema;
};

/**
 * Generate JobPosting schema for job listings
 */
export const generateJobPostingSchema = (job) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "@id": `https://shoppersdiscountclub.directory/jobs/${job.id}`,
    "title": job.title,
    "description": job.description,
    "datePosted": job.created_at || job.date_posted,
    "employmentType": mapJobType(job.job_type || job.type),
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": job.location,
        "addressCountry": "GB"
      }
    },
    "hiringOrganization": {
      "@type": "Organization",
      "name": job.company,
      "url": job.company_website || job.application_url
    }
  };

  // Add optional properties
  if (job.salary_range || job.salary) {
    schema.baseSalary = {
      "@type": "MonetaryAmount",
      "value": job.salary_range || job.salary,
      "currency": "GBP"
    };
  }

  if (job.expires_at || job.valid_until) {
    schema.validThrough = job.expires_at || job.valid_until;
  }

  if (job.application_url) {
    schema.applicationContact = {
      "@type": "ContactPoint",
      "url": job.application_url
    };
  }

  if (job.benefits && job.benefits.length > 0) {
    schema.jobBenefits = job.benefits;
  }

  if (job.requirements && job.requirements.length > 0) {
    schema.qualifications = job.requirements.join(", ");
  }

  return schema;
};

/**
 * Generate Event schema for events/what's on listings
 */
export const generateEventSchema = (event) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Event",
    "@id": `https://shoppersdiscountclub.directory/events/${event.id}`,
    "name": event.title || event.name,
    "description": event.description,
    "startDate": event.date || event.start_date,
    "location": {
      "@type": "Place",
      "name": event.location,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": event.address,
        "addressLocality": extractCity(event.address || event.location),
        "addressCountry": "GB"
      }
    },
    "organizer": {
      "@type": "Organization",
      "name": event.organizer,
      "url": event.website || event.organizer_website
    }
  };

  // Add optional properties
  if (event.end_date) {
    schema.endDate = event.end_date;
  }

  if (event.image_url) {
    schema.image = event.image_url;
  }

  if (event.ticket_price) {
    schema.offers = {
      "@type": "Offer",
      "price": event.ticket_price,
      "priceCurrency": "GBP",
      "availability": "https://schema.org/InStock",
      "url": event.website || event.booking_url
    };
  } else if (event.ticket_price === "Free" || event.is_free) {
    schema.offers = {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "GBP",
      "availability": "https://schema.org/InStock"
    };
  }

  if (event.latitude && event.longitude) {
    schema.location.geo = {
      "@type": "GeoCoordinates",
      "latitude": event.latitude,
      "longitude": event.longitude
    };
  }

  return schema;
};

/**
 * Generate BlogPosting schema for blog articles
 */
export const generateBlogPostingSchema = (post) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `https://shoppersdiscountclub.directory/blog/${post.id}`,
    "headline": post.title,
    "description": post.excerpt,
    "author": {
      "@type": "Person",
      "name": post.author || "Shoppers Discount Club Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Shoppers Discount Club",
      "url": "https://shoppersdiscountclub.directory",
      "logo": {
        "@type": "ImageObject",
        "url": "https://shoppersdiscountclub.directory/logo.png"
      }
    },
    "datePublished": post.created_at || post.published_date,
    "dateModified": post.updated_at || post.modified_date || post.created_at,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://shoppersdiscountclub.directory/blog/${post.id}`
    }
  };

  // Add optional properties
  if (post.featured_image_url || post.image_url) {
    schema.image = {
      "@type": "ImageObject",
      "url": post.featured_image_url || post.image_url,
      "width": 1200,
      "height": 630
    };
  }

  if (post.content) {
    schema.articleBody = post.content;
  }

  if (post.category) {
    schema.articleSection = post.category;
  }

  if (post.tags && post.tags.length > 0) {
    schema.keywords = Array.isArray(post.tags) ? post.tags.join(", ") : post.tags;
  }

  return schema;
};

/**
 * Generate structured data for business directory page
 */
export const generateBusinessDirectorySchema = (businesses) => {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": "https://shoppersdiscountclub.directory/directory",
    "name": "Business Directory",
    "description": "Directory of participating supermarkets, retailers, and service providers",
    "numberOfItems": businesses.length,
    "itemListElement": businesses.map((business, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": generateLocalBusinessSchema(business)
    }))
  };
};

/**
 * Generate structured data for jobs page
 */
export const generateJobListingSchema = (jobs) => {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": "https://shoppersdiscountclub.directory/jobs", 
    "name": "Job Opportunities",
    "description": "Current job openings in retail and related sectors",
    "numberOfItems": jobs.length,
    "itemListElement": jobs.map((job, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": generateJobPostingSchema(job)
    }))
  };
};

/**
 * Generate structured data for events page
 */
export const generateEventListingSchema = (events) => {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": "https://shoppersdiscountclub.directory/events",
    "name": "Events & What's On",
    "description": "Upcoming events, markets, fairs, and local activities",
    "numberOfItems": events.length,
    "itemListElement": events.map((event, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": generateEventSchema(event)
    }))
  };
};

/**
 * Utility function to extract city from address string
 */
const extractCity = (address) => {
  if (!address) return "";
  const parts = address.split(",");
  return parts.length > 1 ? parts[parts.length - 2].trim() : parts[0].trim();
};

/**
 * Map internal job types to schema.org employment types
 */
const mapJobType = (jobType) => {
  const typeMap = {
    'full-time': 'FULL_TIME',
    'part-time': 'PART_TIME',
    'contract': 'CONTRACTOR',
    'temporary': 'TEMPORARY',
    'intern': 'INTERN'
  };
  return typeMap[jobType?.toLowerCase()] || 'FULL_TIME';
};

/**
 * Component to render structured data script tag
 */
export const StructuredDataScript = ({ data }) => {
  if (!data) return null;
  
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(data, null, 2)
      }}
    />
  );
};